const DEFAULT_SAFE_ERROR_MESSAGE = 'Something went wrong. Please try again.';

const DUPLICATE_CONSTRAINT_MESSAGES: Record<string, string> = {
  colleges_college_code_key:
    'A college with this code already exists. Please use a different college code.',
  candidates_email_key:
    'This email is already registered. Please use a different email.',
  candidates_enrollment_number_key:
    'This enrollment number is already registered. Please verify your details.',
  candidates_phone_key:
    'This phone number is already registered. Please use a different phone number.',
};

const SENSITIVE_ERROR_PATTERNS = [
  'duplicate key value violates unique constraint',
  'violates foreign key constraint',
  'violates check constraint',
  'sqlstate',
  'postgres',
  'supabase',
  'constraint',
  'relation "',
  'column "',
  'schema "',
  'syntax error',
  'permission denied for',
  'invalid input syntax for type',
  'service_role',
  'apikey',
  'jwt',
];

const toErrorRecord = (
  error: unknown
): Partial<{
  code: string | number;
  message: string;
  details: string;
  hint: string;
  error: string;
  error_description: string;
}> => (typeof error === 'object' && error !== null ? error : {});

const findDuplicateConstraintMessage = (text: string): string | null => {
  const lowerText = text.toLowerCase();

  for (const [constraint, message] of Object.entries(
    DUPLICATE_CONSTRAINT_MESSAGES
  )) {
    if (lowerText.includes(constraint.toLowerCase())) {
      return message;
    }
  }

  return null;
};

const extractErrorText = (error: unknown): string => {
  if (typeof error === 'string') return error;

  const record = toErrorRecord(error);
  return (
    record.message ||
    record.error_description ||
    record.error ||
    record.details ||
    record.hint ||
    ''
  );
};

const isSensitiveErrorText = (message: string): boolean => {
  const lowerMessage = message.toLowerCase();
  return SENSITIVE_ERROR_PATTERNS.some((pattern) =>
    lowerMessage.includes(pattern)
  );
};

export const getSafeErrorMessage = (
  error: unknown,
  fallback: string = DEFAULT_SAFE_ERROR_MESSAGE
): string => {
  const errorText = extractErrorText(error).trim();
  if (!errorText) return fallback;

  const duplicateMessage = findDuplicateConstraintMessage(errorText);
  if (duplicateMessage) return duplicateMessage;

  const errorRecord = toErrorRecord(error);
  if (
    String(errorRecord.code ?? '') === '23505' ||
    errorText.toLowerCase().includes('duplicate key')
  ) {
    return 'A record with this value already exists. Please use a different value.';
  }

  if (isSensitiveErrorText(errorText)) {
    return fallback;
  }

  return errorText;
};
